import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  CustomExperimentSpec,
  PanelRow,
  ActionBarSpec,
} from '@learnforge/demo-render/harness';
import { createReadout, type ReadoutHandle, type ReadoutRow } from '@learnforge/demo-render';
import { createSvgEl } from '@learnforge/demo-render';
import { niceCeil } from '@learnforge/demo-math/numeric';
import type { FinanceContext } from '../types';
import type { FinanceKey } from '../locales/keys';
import { createChart, type ChartHandle } from '../render/chart';
import { createLineSeries, type LineSeriesHandle } from '../render/line-series';
import { createAreaStack, type AreaStackHandle, type Band } from '../render/area-stack';
import { createCoinIcon } from '../render/coin';
import { levelPayment, amortizationSchedule, minimumPaymentPayoff } from '../finance/loan';
import { type Point } from '../finance/series';
import { formatMoney, formatPercent, formatYears } from '../finance/format';

// ── State ─────────────────────────────────────────────────────────────────────

export interface BorrowingCostState {
  amount: number;
  apr: number;
  years: number;
  mode: 'amortizing' | 'credit-card'; // credit-card is advanced
  minRate: number; // credit-card min as % of balance
  extra: number; // extra payment per month (advanced)
}

// ── Pure model (unit-tested) ──────────────────────────────────────────────────

export interface BorrowingCostModel {
  bands: Band[];
  balanceLine: Point[];
  readout: ReadoutRow[];
  /** Effective horizon in years (may exceed state.years for credit-card mode) */
  horizon: number;
}

/**
 * Build the model for the borrowing-cost experiment.
 *
 * Amortizing mode: runs a standard amortization schedule (with optional extra
 * payment), aggregates monthly rows into per-year cumulative interest and
 * principal bands, and produces a declining balance line.
 *
 * Credit-card mode: runs minimum-payment payoff, aggregates similarly.
 */
export function borrowingCostModel(state: BorrowingCostState): BorrowingCostModel {
  const { amount, apr, years, mode, minRate, extra } = state;
  const MIN_FLOOR = 25;

  if (mode === 'credit-card') {
    return buildCreditCardModel(amount, apr, minRate, MIN_FLOOR);
  }

  return buildAmortizingModel(amount, apr, years, extra);
}

function buildAmortizingModel(
  amount: number,
  apr: number,
  years: number,
  extra: number,
): BorrowingCostModel {
  const periodsPerYear = 12;

  // Baseline schedule (no extra) for savings comparison
  const baseSchedule = amortizationSchedule(amount, apr, periodsPerYear, years);
  const basePayment = levelPayment(amount, apr, periodsPerYear, years);
  const baseTotalInterest = baseSchedule.reduce((s, r) => s + r.interest, 0);

  // Extra-payment schedule: rebuild with augmented payment
  let effectiveSchedule = baseSchedule;
  let effectivePayment = basePayment;

  if (extra > 0) {
    // Simulate manually with extra payment
    const i = apr / periodsPerYear;
    const augmentedPayment = basePayment + extra;
    const rows = [];
    let balance = amount;
    let period = 1;
    const maxPeriods = periodsPerYear * years;

    while (balance > 0.005 && period <= maxPeriods) {
      const interest = balance * i;
      const payment = Math.min(augmentedPayment, balance + interest);
      const principalPaid = payment - interest;
      balance -= principalPaid;
      rows.push({
        period,
        interest,
        principal: principalPaid,
        balance: Math.max(balance, 0),
      });
      period++;
    }
    effectiveSchedule = rows;
    effectivePayment = augmentedPayment;
  }

  // Aggregate to per-year cumulative bands
  const yearCount = years; // always show full term on x-axis
  const interestPoints: Point[] = [{ t: 0, value: 0 }];
  const principalPoints: Point[] = [{ t: 0, value: 0 }];
  const balanceLine: Point[] = [{ t: 0, value: amount }];

  let cumInterest = 0;
  let cumPrincipal = 0;

  for (let yr = 1; yr <= yearCount; yr++) {
    const start = (yr - 1) * periodsPerYear;
    const end = yr * periodsPerYear;
    for (let p = start; p < end; p++) {
      const row = effectiveSchedule[p];
      if (row !== undefined) {
        cumInterest += row.interest;
        cumPrincipal += row.principal;
      }
    }
    interestPoints.push({ t: yr, value: cumInterest });
    principalPoints.push({ t: yr, value: cumPrincipal });
    // Balance at end of year yr
    const lastRow = effectiveSchedule[Math.min(end, effectiveSchedule.length) - 1];
    balanceLine.push({ t: yr, value: lastRow !== undefined ? lastRow.balance : 0 });
  }

  const bands: Band[] = [
    { id: 'interest', points: interestPoints, className: 'fin-area-interest' },
    { id: 'principal', points: principalPoints, className: 'fin-area-principal' },
  ];

  const totalInterest = effectiveSchedule.reduce((s, r) => s + r.interest, 0);
  const totalPaid = totalInterest + amount;

  const readout: ReadoutRow[] = [
    {
      id: 'monthly-payment',
      lhs: 'stat.monthly-payment.label',
      relation: '=',
      rhs: formatMoney(effectivePayment),
    },
    {
      id: 'total-paid',
      lhs: 'stat.total-paid.label',
      relation: '=',
      rhs: formatMoney(totalPaid),
    },
    {
      id: 'total-interest',
      lhs: 'stat.total-interest.label',
      relation: '=',
      rhs: formatMoney(totalInterest),
    },
  ];

  if (extra > 0) {
    const interestSaved = baseTotalInterest - totalInterest;
    const baseActualPeriods = baseSchedule.length;
    const extraActualPeriods = effectiveSchedule.length;
    const yearsSaved = (baseActualPeriods - extraActualPeriods) / periodsPerYear;

    if (interestSaved > 0) {
      readout.push({
        id: 'interest-saved',
        lhs: 'stat.interest-saved.label',
        relation: '=',
        rhs: formatMoney(interestSaved),
        emphasis: 'invariant',
      });
    }
    if (yearsSaved > 0.08) {
      readout.push({
        id: 'years-saved',
        lhs: 'stat.years-saved.label',
        relation: '=',
        rhs: formatYears(yearsSaved),
        emphasis: 'invariant',
      });
    }
  }

  return { bands, balanceLine, readout, horizon: yearCount };
}

function buildCreditCardModel(
  amount: number,
  apr: number,
  minRate: number,
  minFloor: number,
): BorrowingCostModel {
  const periodsPerYear = 12;
  const result = minimumPaymentPayoff(amount, apr, periodsPerYear, minRate, minFloor);

  const payoffYears = result.periods / periodsPerYear;
  const displayHorizon = Math.ceil(payoffYears);
  const horizon = Math.max(displayHorizon, 5); // at least 5 years for display

  // Aggregate to per-year cumulative bands
  const interestPoints: Point[] = [{ t: 0, value: 0 }];
  const principalPoints: Point[] = [{ t: 0, value: 0 }];
  const balanceLine: Point[] = [{ t: 0, value: amount }];

  let cumInterest = 0;
  let cumPrincipal = 0;

  for (let yr = 1; yr <= horizon; yr++) {
    const start = (yr - 1) * periodsPerYear;
    const end = yr * periodsPerYear;
    for (let p = start; p < end; p++) {
      const row = result.schedule[p];
      if (row !== undefined) {
        cumInterest += row.interest;
        // principal can be negative if payment < interest (never-pays-off case)
        cumPrincipal += Math.max(row.principal, 0);
      }
    }
    interestPoints.push({ t: yr, value: cumInterest });
    principalPoints.push({ t: yr, value: cumPrincipal });
    const lastRow = result.schedule[Math.min(end, result.schedule.length) - 1];
    balanceLine.push({ t: yr, value: lastRow !== undefined ? Math.max(lastRow.balance, 0) : 0 });
  }

  const bands: Band[] = [
    { id: 'interest', points: interestPoints, className: 'fin-area-interest' },
    { id: 'principal', points: principalPoints, className: 'fin-area-principal' },
  ];

  const readout: ReadoutRow[] = [
    {
      id: 'monthly-payment',
      lhs: 'stat.monthly-payment.label',
      relation: '≈',
      rhs: formatMoney(Math.max(amount * minRate, minFloor)),
    },
    {
      id: 'total-paid',
      lhs: 'stat.total-paid.label',
      relation: '=',
      rhs: formatMoney(result.totalInterest + amount),
    },
    {
      id: 'total-interest',
      lhs: 'stat.total-interest.label',
      relation: '=',
      rhs: formatMoney(result.totalInterest),
    },
    {
      id: 'payoff-years',
      lhs: 'stat.payoff-years.label',
      relation: '=',
      rhs: result.paysOff ? formatYears(payoffYears) : '∞',
      ...(result.paysOff ? {} : { emphasis: 'broken' as const }),
    },
  ];

  return { bands, balanceLine, readout, horizon };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const CHART_OPTS = {
  width: 800,
  height: 460,
  padding: { top: 24, right: 32, bottom: 44, left: 80 },
  yTickCount: 5,
  xTickCount: 6,
} as const;

function defaultState(): BorrowingCostState {
  return {
    amount: 10000,
    apr: 0.06,
    years: 5,
    mode: 'amortizing',
    minRate: 0.02,
    extra: 0,
  };
}

const PRESETS = [
  { id: 'car-loan', labelKey: 'preset.car-loan.label' as FinanceKey },
  { id: 'mortgage', labelKey: 'preset.mortgage.label' as FinanceKey },
  { id: 'credit-card-trap', labelKey: 'preset.credit-card-trap.label' as FinanceKey },
];

function applyPresetLocal(state: BorrowingCostState, id: string): BorrowingCostState {
  switch (id) {
    case 'car-loan':
      return {
        ...state,
        amount: 15000,
        apr: 0.07,
        years: 4,
        mode: 'amortizing',
        extra: 0,
      };
    case 'mortgage':
      return {
        ...state,
        amount: 200000,
        apr: 0.035,
        years: 25,
        mode: 'amortizing',
        extra: 0,
      };
    case 'credit-card-trap':
      return {
        ...state,
        amount: 5000,
        apr: 0.2,
        years: 5,
        mode: 'credit-card',
        minRate: 0.02,
        extra: 0,
      };
    default:
      return state;
  }
}

function randomise(state: BorrowingCostState, prng: (max?: number) => number): BorrowingCostState {
  const amounts = [5000, 8000, 10000, 15000, 20000, 25000];
  const rates = [0.04, 0.05, 0.06, 0.07, 0.08, 0.1];
  const termOptions = [3, 4, 5, 7, 10, 15];
  return {
    ...state,
    amount: amounts[Math.floor(prng() * amounts.length)] ?? 10000,
    apr: rates[Math.floor(prng() * rates.length)] ?? 0.06,
    years: termOptions[Math.floor(prng() * termOptions.length)] ?? 5,
  };
}

// ── Spec factory ──────────────────────────────────────────────────────────────

function buildSpec(): CustomExperimentSpec<BorrowingCostState, FinanceKey, FinanceContext> {
  return {
    labelKey: 'experiment.borrowing-cost.label',
    demoId: 'finance',
    advancedKey: 'borrowing-cost',

    initialState: (_ctx) => defaultState(),

    applyPreset: (state, id) => applyPresetLocal(state, id),

    panel(state, ctx): PanelRow<FinanceKey, BorrowingCostState>[] {
      const rows: PanelRow<FinanceKey, BorrowingCostState>[] = [
        {
          kind: 'slider',
          id: 'amount',
          labelKey: 'param.amount.label',
          min: 1000,
          max: 500000,
          step: 1000,
          value: state.amount,
          format: formatMoney,
          onChange: (v) => ({ ...state, amount: v }),
        },
        {
          kind: 'slider',
          id: 'apr',
          labelKey: 'param.apr.label',
          min: 0.01,
          max: 0.3,
          step: 0.005,
          value: state.apr,
          format: (v) => formatPercent(v, 1),
          onChange: (v) => ({ ...state, apr: v }),
        },
        {
          kind: 'slider',
          id: 'years',
          labelKey: 'param.years.label',
          min: 1,
          max: 30,
          step: 1,
          value: state.years,
          format: formatYears,
          onChange: (v) => ({ ...state, years: v }),
        },
        {
          kind: 'advanced-toggle',
          labelKey: 'panel.advanced',
        },
      ];

      if (ctx.advanced) {
        rows.splice(3, 0, {
          kind: 'dropdown',
          id: 'mode',
          labelKey: 'param.mode.label',
          options: [
            { value: 'amortizing', labelKey: 'param.mode.amortizing' as FinanceKey },
            { value: 'credit-card', labelKey: 'param.mode.credit-card' as FinanceKey },
          ],
          value: state.mode,
          onChange: (v) => ({ ...state, mode: v as BorrowingCostState['mode'] }),
        });

        if (state.mode === 'credit-card') {
          rows.splice(4, 0, {
            kind: 'slider',
            id: 'minRate',
            labelKey: 'param.minRate.label',
            min: 0.01,
            max: 0.1,
            step: 0.005,
            value: state.minRate,
            format: (v) => formatPercent(v, 1),
            onChange: (v) => ({ ...state, minRate: v }),
          });
        }

        if (state.mode === 'amortizing') {
          rows.splice(4, 0, {
            kind: 'slider',
            id: 'extra',
            labelKey: 'param.extra.label',
            min: 0,
            max: 2000,
            step: 50,
            value: state.extra,
            format: formatMoney,
            onChange: (v) => ({ ...state, extra: v }),
          });
        }
      }

      return rows;
    },

    actions(_state, ctx): ActionBarSpec<FinanceKey, BorrowingCostState> {
      return {
        onReset: () => defaultState(),
        onRandomise: (s) => randomise(s, ctx.prng),
        presets: PRESETS,
        onPreset: (s, id) => applyPresetLocal(s, id),
      };
    },

    applyShotState(state): BorrowingCostState {
      // Amortizing pose: 5-year loan at 8%, shows interest-heavy early years
      return {
        ...state,
        amount: 15000,
        apr: 0.08,
        years: 5,
        mode: 'amortizing',
        extra: 0,
      };
    },

    createRender(ctx, host, _controls) {
      // Build chart
      const chart: ChartHandle = createChart(host, {
        ...CHART_OPTS,
        formatX: (v) => formatYears(v),
        formatY: (v) => formatMoney(v),
      });

      // Coin icon at the y-axis head
      const coinGroup = createCoinIcon(18);
      coinGroup.setAttribute(
        'transform',
        `translate(${CHART_OPTS.padding.left - 4}, ${CHART_OPTS.padding.top})`,
      );
      chart.svg.appendChild(coinGroup);

      // Stacked area layer (interest + principal bands)
      const areaStack: AreaStackHandle = createAreaStack(chart);

      // Line series for the declining balance
      const lines: LineSeriesHandle = createLineSeries(chart);

      // Legend group
      const legendGroup = createSvgEl('g', { class: 'fin-legend' });
      chart.svg.appendChild(legendGroup);

      // Readout — lives OUTSIDE ctx.root per the harness contract
      const readout: ReadoutHandle = createReadout(document.getElementById('readout') ?? host);

      function updateLegend(): void {
        while (legendGroup.firstChild) {
          legendGroup.removeChild(legendGroup.firstChild);
        }
        const { width, padding } = CHART_OPTS;
        // Right-anchor so labels stay in the empty upper-right space.
        const labelRightX = width - padding.right - 8;
        const swatchLen = 16;
        const swatchGap = 6;
        const legendY = padding.top + 16;

        const items: Array<{ cls: string; key: FinanceKey; isLine?: boolean }> = [
          { cls: 'fin-area-interest', key: 'series.interest.label' },
          { cls: 'fin-area-principal', key: 'series.principal.label' },
          { cls: 'fin-line-balance fin-legend-dot', key: 'series.balance.label', isLine: true },
        ];

        items.forEach((item, i) => {
          const rowY = legendY + i * 18;
          const swatchX2 = labelRightX - swatchGap;
          const swatchX1 = swatchX2 - swatchLen;

          if (item.isLine) {
            const lineEl = createSvgEl('line', {
              class: item.cls,
              x1: swatchX1,
              y1: rowY,
              x2: swatchX2,
              y2: rowY,
            });
            legendGroup.appendChild(lineEl);
          } else {
            const rect = createSvgEl('rect', {
              class: item.cls,
              x: swatchX1,
              y: rowY - 5,
              width: swatchLen,
              height: 10,
              rx: 2,
            });
            legendGroup.appendChild(rect);
          }

          const label = createSvgEl('text', {
            class: 'fin-legend',
            x: labelRightX,
            y: rowY,
            'text-anchor': 'end',
            'dominant-baseline': 'middle',
          });
          label.textContent = ctx.i18n.t(item.key);
          legendGroup.appendChild(label);
        });
      }

      return {
        update(state: BorrowingCostState): void {
          const m = borrowingCostModel(state);

          // Compute y-axis max from the stacked total (cumulative interest + principal)
          const interestBand = m.bands.find((b) => b.id === 'interest');
          const principalBand = m.bands.find((b) => b.id === 'principal');
          const maxStacked =
            (interestBand?.points.at(-1)?.value ?? 0) + (principalBand?.points.at(-1)?.value ?? 0);
          const yMax = niceCeil(Math.max(maxStacked, state.amount) * 1.1);

          chart.setScales({
            xDomain: [0, m.horizon],
            yDomain: [0, yMax],
          });

          areaStack.setBands(m.bands);
          lines.setSeries('balance', m.balanceLine, 'fin-line-balance');

          updateLegend();
          readout.update(m.readout.map((r) => ({ ...r, lhs: ctx.i18n.t(r.lhs as FinanceKey) })));
        },

        dispose(): void {
          readout.destroy();
          areaStack.destroy();
          lines.destroy();
          chart.destroy();
        },
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
